import { TagGroup } from "@certn/cascadia-react/components/tag-group/TagGroup";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import i18n from "../../i18n";
import { OrderingCheckCard } from "../ordering-check-card";
import { ActionList, ActionListItem, Stack, Text } from "@certn/cascadia";
import { components } from "packages/api/generated-schemas/client-portal";
import { PreviewRubricModal } from "../preview-adjudication-rubric/PreviewRubricModal";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCanViewClientPortalPricing } from "../permissions/Permissions";
import { formatClientInputClaimValue } from "../../utils/formatClientInputClaim";
import { formatNullableCurrency } from "../../utils/formatNullableCurrency";

export interface PackageDetailsProps {
  name: string;
  description: string;
  price: {
    price: number | null;
    currency: string;
  };
  tags: { id: string; tagValue: string }[];
  checks: Record<string, any>[];
  hasBigName?: boolean;
  adjudicationRubric?: components["schemas"]["PackageAdjudicationRubric"];
  isAtsPackage?: boolean;
  isApiPackage?: boolean;
  clientInputClaims?: Record<string, unknown>;
  useExactPriceLabel?: boolean;
}

export function PackageDetails({
  name,
  description,
  price,
  tags,
  checks,
  hasBigName = false,
  adjudicationRubric,
  isAtsPackage = false,
  isApiPackage = false,
  clientInputClaims,
  useExactPriceLabel = false,
}: PackageDetailsProps) {
  const { t } = useTranslation();
  const configurationSummary =
    isAtsPackage && isApiPackage
      ? t("This package is configured for ATS integrations and API orders.")
      : isApiPackage
        ? t("This package is configured for API orders.")
        : t("This package is configured for ATS integrations.");
  const { enableClientAdjudication } = useFlags();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const formattedPrice = formatNullableCurrency(price.price, {
    currency: price.currency,
    locale: i18n.language,
    fallback: null,
  });

  return (
    <div className={css(styles.flexColumn, styles.container)}>
      <div className={css(styles.flexColumn, styles.titleSection)}>
        <div className={styles.flexColumn}>
          <Text style="summary/section-title" color="text-accent-tofino">
            {t("PACKAGE")}
          </Text>
          <Text
            style={hasBigName ? "global/heading-large" : "global/title-small"}
            testID="package-details-name"
          >
            {name}
          </Text>
          <Text
            style="global/heading-small"
            color="text-accent-tofino"
            testID="package-details-price"
          >
            {canViewClientPortalPricing && formattedPrice
              ? `${useExactPriceLabel ? t("Price") : t("From")} ${formattedPrice}`
              : ""}
          </Text>
        </div>
        {tags && (
          <TagGroup
            handleRemove={() => undefined}
            isDisabled
            values={tags}
            testID="package-details-tag-group"
          />
        )}
        {description && <Text style="form/body-primary">{description}</Text>}
        {(isAtsPackage || isApiPackage) && (
          <div className={styles.atsSection}>
            <Text style="global/heading-small" color="text-accent-tofino">
              {t("Configuration")}
            </Text>
            <Text style="form/body-primary">{configurationSummary}</Text>
            {clientInputClaims && Object.keys(clientInputClaims).length > 0 && (
              <div className={styles.claimsSection}>
                <Text style="form/body-secondary">
                  {t("Pre-configured input fields:")}
                </Text>
                <div className={styles.claimsList}>
                  {Object.entries(clientInputClaims).map(([key, value]) => {
                    const formattedValue = formatClientInputClaimValue(
                      key,
                      value,
                      {
                        locale: i18n.language,
                        t,
                      }
                    );

                    return (
                      <div
                        key={key}
                        className={styles.claimItem}
                        data-testid={`ats-claim-${key}`}
                      >
                        <Text style="form/body-primary">
                          <strong>{key}:</strong>
                          {formattedValue ? ` ${formattedValue}` : ""}
                        </Text>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {adjudicationRubric && enableClientAdjudication && (
        <div>
          <Stack gap="space-3">
            <Text style="global/heading-small">
              {t("Adjudication applied")}
            </Text>
            <ActionList>
              <ActionListItem
                title={adjudicationRubric.name || "--"}
                trailing={
                  <PreviewRubricModal rulesets={adjudicationRubric.rulesets} />
                }
              />
            </ActionList>
          </Stack>
        </div>
      )}
      <div className={css(styles.flexColumn, styles.checkCardList)}>
        <Text style="global/heading-small">{t("Checks included")}</Text>
        {checks?.map((check) => {
          return (
            <OrderingCheckCard
              key={check.type}
              isDisabled
              isSelected
              isReadOnly
              name={check.name}
              type={check.type}
              turnaroundTime={check.estimated_turnaround_time}
              disclaimersIconList={check.disclaimers_icon_list}
              disclaimers={check.disclaimers}
              description={check.description}
              infoParagraphs={check.info_paragraphs}
              coverage={check.coverage}
              keyFeatures={check.key_features}
              howItCanHelpYou={check.how_it_can_help_you}
              umbrellaChecksSupported={[]}
              testID={slugify(check.type)}
              selectedArgument={check.selected_option}
              selectFieldInformation={check.select_field_information}
              selectedOptions={check.selected_options || check.selectedOptions}
              currency={check.currency || price.currency}
              checkArguments={check.check_arguments}
              addOnOptions={check.add_on_options}
            />
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  container: css({
    gap: "var(--space-6)",
  }),
  titleSection: css({
    gap: "var(--space-3)",
  }),
  checkCardList: css({
    gap: "var(--space-3)",
  }),
  flexColumn: css({
    display: "flex",
    flexDirection: "column",
  }),
  atsSection: css({
    display: "flex",
    flexDirection: "column",
    padding: "var(--space-4)",
    backgroundColor: "var(--surface-sunken)",
    borderRadius: "var(--border-radius-md)",
    gap: "var(--space-3)",
  }),
  claimsSection: css({
    display: "flex",
    flexDirection: "column",
    marginTop: "var(--space-2)",
    gap: "var(--space-2)",
  }),
  claimsList: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  claimItem: css({
    padding: "var(--space-2)",
    backgroundColor: "var(--surface-base)",
    borderRadius: "var(--border-radius-sm)",
  }),
};
